import { useState, useEffect, useCallback, useMemo } from 'react';
import { normalize, clamp, lerpByStep, lerp } from '@spa/shared/utils';
import { useDrag } from 'react-use-gesture';

const getDiscreteFactor = (factor, min, max, step) => {
  const value = lerpByStep(min, max, factor, step);

  return normalize(min, max, Math.ceil(value));
};

export const useSlider = (ref, name, min, max, value, onChange, step = 1) => {
  const clampedValue = useMemo(() => clamp(value, min, max), [value]);
  const [isDragging, setIsDragging] = useState(false);
  const [factor, setFactor] = useState();
  const [dragPosition, setDragPosition] = useState(ref.current ? ref.current.offsetWidth * factor : 0);

  const onDragHandler = useCallback(
    ({ down, movement: [mx], memo = factor }) => {
      if (!ref.current) {
        return;
      }

      if (isDragging !== down) {
        setIsDragging(down);
      }

      const width = ref.current.offsetWidth;
      const deltaX = normalize(0, width, mx);
      const factorWithinBounds = clamp(memo + deltaX, 0, 1);
      const newFactor = step ? getDiscreteFactor(factorWithinBounds, min, max, step) : factorWithinBounds;

      setFactor(newFactor);

      return memo;
    },
    [isDragging, factor]
  );

  const onDrag = useDrag(onDragHandler);

  useEffect(() => setFactor(normalize(min, max, clampedValue)), [clampedValue]);

  useEffect(() => {
    const noFactor = isNaN(factor);
    const finalValue = noFactor ? clampedValue : Math.round(lerp(min, max, factor));

    if (!isDragging && clampedValue === finalValue) {
      return;
    }

    onChange(isDragging || clampedValue === finalValue, name, finalValue);
  }, [factor, isDragging]);

  useEffect(() => {
    const width = ref.current ? ref.current.offsetWidth : 0;
    const newDragPosition = lerp(0, width, factor);

    setDragPosition(newDragPosition);
  }, [factor, onChange]);

  useEffect(() => {
    const onResize = () => {
      const width = ref.current ? ref.current.offsetWidth : 0;
      const newDragPosition = lerp(0, width, factor);

      setDragPosition(newDragPosition);
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [factor]);

  return {
    onDrag,
    factor,
    value: Math.trunc(lerp(min, max, factor)),
    percentage: lerp(0, 100, factor),
    dragPosition
  };
};
